/**
 * Top-of-page KPI row for the Financing page. Four tiles per design:
 * Total Loan Balance · Monthly Debt Service · Avg Interest Rate ·
 * Financed Properties.
 */
import {
  Building2,
  CalendarClock,
  HandCoins,
  Percent,
} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type { FinancingKpis } from "./financing-metrics";

function fmtCurrency(value: number): string {
  const sign = value < 0 ? "-" : "";
  return sign + "$" + Math.abs(Math.round(value)).toLocaleString();
}

function fmtPct(value: number, digits = 2): string {
  return `${value.toFixed(digits)}%`;
}

interface FinancingKpiRowProps {
  kpis: FinancingKpis;
}

export function FinancingKpiRow({ kpis }: FinancingKpiRowProps) {
  const freeAndClear = kpis.ownedCount - kpis.financedCount;

  const tiles: Array<{
    label: string;
    icon: typeof Building2;
    value: string;
    valueTone?: "neg";
    sub: string;
    accentSplit?: string;
  }> = [
    {
      label: "Total Loan Balance",
      icon: HandCoins,
      value: fmtCurrency(kpis.totalLoanBalance),
      sub: `${kpis.financedCount} active ${kpis.financedCount === 1 ? "loan" : "loans"}`,
    },
    {
      label: "Monthly Debt Service",
      icon: CalendarClock,
      value: fmtCurrency(kpis.totalMonthlyPayment),
      valueTone: "neg",
      sub: `${fmtCurrency(kpis.totalMonthlyPayment * 12)}/yr across all loans`,
    },
    {
      label: "Avg Interest Rate",
      icon: Percent,
      value: kpis.financedCount > 0 ? fmtPct(kpis.avgRate) : "—",
      sub: "Weighted by loan balance",
    },
    {
      label: "Financed Properties",
      icon: Building2,
      value: String(kpis.financedCount),
      accentSplit: ` / ${kpis.ownedCount}`,
      sub: `${freeAndClear} owned free & clear`,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {tiles.map((tile) => (
        <Card key={tile.label} className="shadow-soft">
          <CardContent className="flex flex-col gap-1.5 p-4">
            <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <tile.icon className="h-3.5 w-3.5 text-primary" aria-hidden />
              {tile.label}
            </span>
            <span
              className={cn(
                "text-2xl font-extrabold leading-tight tabular-nums",
                tile.valueTone === "neg" ? "text-destructive" : "text-foreground",
              )}
            >
              {tile.value}
              {tile.accentSplit && (
                <span className="ml-1 text-sm font-semibold text-muted-foreground">
                  {tile.accentSplit}
                </span>
              )}
            </span>
            <span className="text-xs text-muted-foreground">{tile.sub}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
